import React from "react";
import {graphql} from "react-apollo";
import {flowRight as compose} from "lodash";
import {formValueSelector} from "redux-form/immutable";
import gql from "graphql-tag";
import {connect} from "react-redux";
import {fromJS, List} from "immutable";
import Form from "../../../customers/forms/ShippingAddressForm";
import Loading from "../../../components/Loading";
import GraphQLErrors from "../../../components/GraphQLErrors";
import {cancelEditPartnerAddress, successEditPartnerAddress} from "../../../actions/PartnerApiActions";
import {GET_PARTNER} from "../../../queries/partners";
import {createValidationErrors, createErrors} from "../../../lib/ErrorFormatter";
import {getUSStates, getCanadaProvinces, isUS, isCanada} from "../../../lib/CountriesAndStates";
import {hasStateProvince} from "../../../lib/AddressRules";
import {validateShippingAddress} from "../../../lib/AddressValidation";
import {validatePresence} from "../../../lib/Validators";
import {coerceBoolean} from "../../../components/forms/BooleanInputField";

// A partner's saved address, in the same form as a new shipment's destination
// (country, state/province by country, telephone, address validation), with a
// label to know it by. With `addressId`, edits (or deletes) that address.

function validateForm(values) {
  return validatePresence(validateShippingAddress(values), values, "label").toJS();
}

// Stored address fields -> the shipping address form's field names.
function toFormValues(address) {
  return fromJS({
    label: address.label || "",
    type: address.type || "business",
    name: address.name || "",
    attName: address.attName || "",
    phoneNr: address.phoneNr || "",
    email: address.email || "",
    addressAddressLine1: address.addressLine1 || "",
    addressAddressLine2: address.addressLine2 || "",
    addressCity: address.city || "",
    addressStateProvince: address.stateProvince || "",
    addressPostcode: address.postcode || "",
    addressCountryCode: address.countryCode || "",
    skipAddressValidation: false,
  });
}

const FIELDS = ["label", "type", "name", "attName", "phoneNr", "email", "addressAddressLine1", "addressAddressLine2",
  "addressCity", "addressStateProvince", "addressPostcode", "addressCountryCode"];

class PartnerAddressFormContainer extends React.Component {

  constructor(props) {
    super(props);
    this.state = { errors: [] }
  }

  handleSubmit(values) {
    const data = FIELDS.reduce((acc, field) => ({...acc, [field]: values.get(field) || ""}), {
      partnerId: this.props.partnerId,
      addressId: this.props.addressId || null,
      skipAddressValidation: coerceBoolean(values.get("skipAddressValidation")),
    });

    return this.props.savePartnerAddress(data)
      .then((response) => {
        const errors = response.data.savePartnerAddress.errors;
        if (errors.length <= 0) this.props.successEditPartnerAddress();
        else this.setState({errors: createValidationErrors(errors)});
      })
      .catch((err) => this.setState({errors: createErrors(err)}));
  }

  handleDelete() {
    return this.props.removePartnerAddress({partnerId: this.props.partnerId, addressId: this.props.addressId})
      .then((response) => {
        const errors = response.data.removePartnerAddress.errors;
        if (errors.length <= 0) this.props.successEditPartnerAddress();
        else this.setState({errors: createValidationErrors(errors, errors.map((e) => e.message).join("\n"))});
      })
      .catch((err) => this.setState({errors: createErrors(err)}));
  }

  render() {
    const {countriesData, partnerData} = this.props;
    for (const data of [countriesData, partnerData]) {
      if (data && data.loading) return <Loading />
      if (data && data.error) return <GraphQLErrors error={data.error} />
    }

    const addresses = (partnerData && partnerData.partner && partnerData.partner.addresses) || [];
    const address = addresses.find((a) => a.addressId === this.props.addressId);

    let stateProvinces = List();
    if (this.props.showUSStates) stateProvinces = getUSStates();
    if (this.props.showCanadaProvinces) stateProvinces = getCanadaProvinces();

    return (
      <Form
        initialValues={address ? toFormValues(address) : fromJS({type: "business", skipAddressValidation: false})}
        errors={this.state.errors}
        validate={validateForm}
        countries={fromJS(countriesData.countriesInShops)}
        stateProvinces={stateProvinces}
        showLabel={true}
        showAttentionName={true}
        showStateProvince={this.props.showStateProvince}
        showPhoneNr={true}
        showEmail={true}
        showDialCodeMismatchWarning={false}
        showDelete={!!address}
        onDelete={this.handleDelete.bind(this)}
        onSubmit={this.handleSubmit.bind(this)}
        onCancel={() => this.props.cancelEditPartnerAddress()}
        />
    )
  }
}

const GET_COUNTRIES = gql`
  query countriesInShops {
    countriesInShops {
      code
      name
    }
  }
`;

const SAVE_PARTNER_ADDRESS = gql`
  mutation savePartnerAddress($data: SavePartnerAddressInput!) {
    savePartnerAddress(input: $data) {
      addressId
      errors { key message }
    }
  }
`;

const REMOVE_PARTNER_ADDRESS = gql`
  mutation removePartnerAddress($data: RemovePartnerAddressInput!) {
    removePartnerAddress(input: $data) {
      errors { key message }
    }
  }
`;

const withData = compose(
  graphql(GET_COUNTRIES, {name: "countriesData", options: () => ({fetchPolicy: "network-only"})}),
  graphql(GET_PARTNER, {
    name: "partnerData",
    skip: (props) => !props.addressId,
    options: (props) => ({fetchPolicy: "network-only", variables: {partnerId: props.partnerId}})
  }),
  graphql(SAVE_PARTNER_ADDRESS, {props: ({mutate}) => ({savePartnerAddress: (data) => mutate({variables: {data}})})}),
  graphql(REMOVE_PARTNER_ADDRESS, {props: ({mutate}) => ({removePartnerAddress: (data) => mutate({variables: {data}})})}),
);

// The shared form's own name: its country decides which state/province field shows.
const selector = formValueSelector("ShippingAddressForm");
const mapStateToProps = (state) => ({
  showUSStates: isUS(selector(state, "addressCountryCode")),
  showCanadaProvinces: isCanada(selector(state, "addressCountryCode")),
  showStateProvince: hasStateProvince(selector(state, "addressCountryCode")),
});

export default connect(mapStateToProps, {cancelEditPartnerAddress, successEditPartnerAddress})(withData(PartnerAddressFormContainer));
